import React, { useState, useEffect } from 'react';
import { FileText, Search, Eye, TrendingUp, RefreshCw, Filter, ChevronDown } from 'lucide-react';
import { getPosts, syncPost, triggerTikTokPostsOnlySync, triggerInstagramPostsOnlySync, triggerInstagramTaggedPostsSync, triggerFacebookPostsOnlySync } from '../services/api';

interface Post {
    id: number;
    postId: string;
    socialMediaUserId: string;
    postBody: string | null;
    postDescription: string | null;
    postedAt: string;
    imageUrl: string | null;
    videoUrl: string | null;
    commentsCount: number;
    type: string;
    platform: string;
    permalink: string | null;
    country: string;
    status: string;
}

const PostList: React.FC = () => {
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [platformFilter, setPlatformFilter] = useState<string>('INSTAGRAM');
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [syncingMap, setSyncingMap] = useState<{ [key: string]: boolean }>({});
    const [syncingPlatform, setSyncingPlatform] = useState(false);
    const [selectedSyncPlatform, setSelectedSyncPlatform] = useState<'INSTAGRAM' | 'INSTAGRAM_TAG' | 'FACEBOOK' | 'TIKTOK' | 'TWITTER'>('INSTAGRAM');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(20);

    useEffect(() => {
        setPage(1);
        fetchPosts();
    }, [platformFilter]);

    useEffect(() => {
        fetchPosts();
    }, [page, limit]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (showFilterDropdown && !target.closest('.filter-dropdown-container')) {
                setShowFilterDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showFilterDropdown]);

    const fetchPosts = async () => {
        try {
            setLoading(true);

            const skip = (page - 1) * limit;
            const params: any = {
                skip,
                limit: limit
            };

            if (platformFilter !== 'all') params.platform = platformFilter;
            if (searchTerm) params.search_query = searchTerm;

            const response = await getPosts(params);
            setPosts(response.items);
            setTotal(response.total);
        } catch (error) {
            console.error('Failed to fetch posts', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async (postId: string, platform: string) => {
        try {
            setSyncingMap(prev => ({ ...prev, [postId]: true }));
            // Smart sync with media analysis and AI generation
            const result = await syncPost(postId, false, platform, true, true);

            if (result.status === 'success') {
                // Refresh posts to show updated data (e.g. comments count)
                fetchPosts();

                // Build success message
                let message = '✅ Sync Complete!\n\n';

                // Add media analysis results
                if (result.media_analysis) {
                    const analysis = result.media_analysis;
                    if (analysis.success) {
                        const summary = analysis.analysis_summary;
                        message += `📸 Media Analysis:\n`;
                        message += `  • Type: ${analysis.media_type}\n`;
                        message += `  • Mood: ${summary?.mood || 'N/A'}\n`;
                        message += `  • Objects: ${summary?.objects_detected || 0}\n`;
                        message += `  • Text: ${summary?.text_detected || 0}\n\n`;
                    } else if (analysis.status === 'skipped') {
                        message += `📸 Media: Already analyzed\n\n`;
                    }
                }

                // Add sentiment analysis results
                if (result.sentiment_analysis) {
                    const sentiment = result.sentiment_analysis;
                    if (sentiment.status === 'success' && sentiment.comments_analyzed > 0) {
                        message += `🎭 Sentiment Analysis:\n`;
                        message += `  • Analyzed: ${sentiment.comments_analyzed}\n`;
                        if (sentiment.sentiment_breakdown) {
                            const breakdown = sentiment.sentiment_breakdown;
                            message += `  • Positive: ${breakdown.POSITIVE || 0}\n`;
                            message += `  • Neutral: ${breakdown.NEUTRAL || 0}\n`;
                            message += `  • Negative: ${breakdown.NEGATIVE || 0}\n\n`;
                        }
                    } else if (sentiment.comments_analyzed === 0) {
                        message += `🎭 Sentiment: Already analyzed\n\n`;
                    }
                }

                // Add AI generation results
                if (result.ai_generation) {
                    const aiGen = result.ai_generation;
                    if (aiGen.status === 'success') {
                        message += `🤖 AI Replies Generated:\n`;
                        message += `  • Generated: ${aiGen.comments_generated || 0}\n`;
                        message += `  • Total Processed: ${aiGen.comments_processed || 0}\n`;
                        if (aiGen.comments_failed > 0) {
                            message += `  • Failed: ${aiGen.comments_failed}\n`;
                        }
                    } else if (aiGen.status === 'skipped') {
                        message += `🤖 AI: No comments need replies\n`;
                    }
                }

                alert(message);
            } else if (result.status === 'skipped') {
                alert('Post synced recently. Try again later.');
            }
        } catch (error) {
            console.error('Sync failed', error);
            alert('❌ Sync failed');
        } finally {
            setSyncingMap(prev => ({ ...prev, [postId]: false }));
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchPosts();
    };

    const handleCompleteSync = async () => {
        try {
            setSyncingPlatform(true);
            let result;

            if (selectedSyncPlatform === 'TIKTOK') {
                result = await triggerTikTokPostsOnlySync(true);
            } else if (selectedSyncPlatform === 'INSTAGRAM_TAG') {
                result = await triggerInstagramTaggedPostsSync(true);
            } else if (selectedSyncPlatform === 'FACEBOOK') {
                result = await triggerFacebookPostsOnlySync(true);
            } else if (selectedSyncPlatform === 'TWITTER') {
                result = await import('../services/api').then(m => m.triggerTwitterPostsOnlySync(true));
            } else {
                result = await triggerInstagramPostsOnlySync(true);
            }

            if (result.status === 'success') {
                const platformName = selectedSyncPlatform === 'INSTAGRAM_TAG' ? 'Instagram Tag' : selectedSyncPlatform;
                alert(`${platformName} sync completed! Posts synced: ${result.posts_synced || 0}`);
                // Refresh posts to show updated data
                fetchPosts();
            } else {
                alert(`${selectedSyncPlatform} sync issue: ${result.message || JSON.stringify(result)}`);
            }
        } catch (error: any) {
            console.error(`${selectedSyncPlatform} sync failed`, error);
            alert(`${selectedSyncPlatform} sync failed: ${error.response?.data?.detail || error.message}`);
        } finally {
            setSyncingPlatform(false);
        }
    };

    const formatDate = (dateString: string) => {
        let safeDateString = dateString;
        if (!safeDateString.endsWith('Z') && !safeDateString.includes('+') && !safeDateString.match(/-\d{2}:\d{2}$/)) {
            safeDateString = safeDateString.replace(' ', 'T') + 'Z';
        }

        const date = new Date(safeDateString);
        const formattedDate = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'UTC',
            timeZoneName: 'short'
        });

        const now = new Date();
        const diffInSeconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));

        let timeAgo = '';
        if (diffInSeconds < 60) {
            timeAgo = `${diffInSeconds}s`;
        } else if (diffInSeconds < 3600) {
            timeAgo = `${Math.floor(diffInSeconds / 60)}m`;
        } else if (diffInSeconds < 86400) {
            timeAgo = `${Math.floor(diffInSeconds / 3600)}h`;
        } else {
            const totalDays = Math.floor(diffInSeconds / 86400);
            if (totalDays < 7) {
                timeAgo = `${totalDays}d`;
            } else {
                const weeks = Math.floor(totalDays / 7);
                const remainingDays = totalDays % 7;
                if (remainingDays === 0) {
                    timeAgo = `${weeks}w`;
                } else {
                    timeAgo = `${weeks}w ${remainingDays}d`;
                }
            }
        }

        return `${formattedDate} - ${timeAgo}`;
    };

    return (
        <div className="page-container">
            <div className="page-header" style={{ marginBottom: '1.5rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>Post List</h1>
                    <p className="page-subtitle" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        View and manage social media posts
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <select
                            value={selectedSyncPlatform}
                            onChange={(e) => setSelectedSyncPlatform(e.target.value as 'INSTAGRAM' | 'INSTAGRAM_TAG' | 'FACEBOOK' | 'TIKTOK' | 'TWITTER')}
                            disabled={syncingPlatform}
                            style={{
                                padding: '0.5rem 0.875rem',
                                borderRadius: '8px',
                                border: '1px solid var(--border-color)',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: 'var(--text-primary)',
                                backgroundColor: 'white',
                                cursor: syncingPlatform ? 'not-allowed' : 'pointer',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                minWidth: '160px'
                            }}
                        >
                            <option value="INSTAGRAM">Instagram</option>
                            <option value="FACEBOOK">Facebook</option>
                            <option value="INSTAGRAM_TAG">Instagram Tag</option>
                            <option value="TIKTOK">TikTok</option>
                            <option value="TWITTER">X</option>
                        </select>
                        <button
                            className="btn-primary"
                            onClick={handleCompleteSync}
                            disabled={syncingPlatform}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                            }}
                            title={`Trigger ${selectedSyncPlatform} complete sync`}
                        >
                            <RefreshCw size={16} className={syncingPlatform ? "spin" : ""} />
                            Complete Sync
                        </button>
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: 'white',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: '#E3F2FD'
                        }}>
                            <FileText size={18} color="#2196F3" />
                        </div>
                        <div>
                            <div style={{
                                fontSize: '1.25rem',
                                fontWeight: '700',
                                color: 'var(--text-primary)',
                                lineHeight: '1'
                            }}>
                                {total}
                            </div>
                            <div style={{
                                fontSize: '0.75rem',
                                color: 'var(--text-secondary)',
                                marginTop: '2px'
                            }}>
                                Total Posts
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <Search size={20} />
                    <input
                        type="text"
                        placeholder="Search posts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                </div>
                <div className="filter-dropdown-container" style={{ position: 'relative' }}>
                    <button
                        className="filter-btn"
                        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: platformFilter !== 'all' ? '#4F46E5' : '#fff',
                            color: platformFilter !== 'all' ? '#fff' : '#374151',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                        }}
                    >
                        <Filter size={18} />
                        {platformFilter === 'all' ? 'All Platforms' :
                            platformFilter === 'INSTAGRAM' ? 'Instagram' :
                                platformFilter === 'FACEBOOK' ? 'Facebook' :
                                platformFilter === 'INSTAGRAM_TAG' ? 'Instagram Tag' :
                                    platformFilter === 'TWITTER' ? 'X' : 'TikTok'}
                        <ChevronDown size={16} />
                    </button>

                    {showFilterDropdown && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                marginTop: '8px',
                                backgroundColor: '#fff',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                zIndex: 1000,
                                minWidth: '200px',
                                overflow: 'hidden'
                            }}
                        >
                            {[
                                { value: 'all', label: 'All Platforms' },
                                { value: 'INSTAGRAM', label: 'Instagram' },
                                { value: 'FACEBOOK', label: 'Facebook' },
                                { value: 'INSTAGRAM_TAG', label: 'Instagram Tag' },
                                { value: 'TIKTOK', label: 'TikTok' },
                                { value: 'TWITTER', label: 'X' }
                            ].map((option) => (
                                <div
                                    key={option.value}
                                    onClick={() => {
                                        setPlatformFilter(option.value);
                                        setShowFilterDropdown(false);
                                    }}
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        backgroundColor: platformFilter === option.value ? '#F3F4F6' : '#fff',
                                        color: platformFilter === option.value ? '#4F46E5' : '#374151',
                                        fontWeight: platformFilter === option.value ? '600' : '400',
                                        fontSize: '14px',
                                        transition: 'all 0.15s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (platformFilter !== option.value) {
                                            e.currentTarget.style.backgroundColor = '#F9FAFB';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (platformFilter !== option.value) {
                                            e.currentTarget.style.backgroundColor = '#fff';
                                        }
                                    }}
                                >
                                    {option.label}
                                    {platformFilter === option.value && (
                                        <span style={{ float: 'right', color: '#4F46E5' }}>✓</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="loading-state">Loading posts...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Media</th>
                                <th>Content</th>
                                <th>Platform</th>
                                <th>Posted At</th>
                                <th>Comments</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {posts.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                                        No posts found
                                    </td>
                                </tr>
                            ) : (
                                posts.map((post) => (
                                    <tr key={post.postId}>
                                        <td>
                                            {post.imageUrl && (
                                                <img
                                                    src={post.imageUrl}
                                                    alt="Post"
                                                    style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                                                />
                                            )}
                                            {post.videoUrl && !post.imageUrl && (
                                                <div style={{ width: '60px', height: '60px', backgroundColor: '#f0f0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    📹
                                                </div>
                                            )}
                                            {!post.imageUrl && !post.videoUrl && (
                                                <div style={{ width: '60px', height: '60px', backgroundColor: '#f0f0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FileText size={24} color="#999" />
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ maxWidth: '300px' }}>
                                                <div style={{ fontWeight: '500', marginBottom: '4px' }}>{post.type}</div>
                                                <div style={{ fontSize: '0.875rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {post.postBody || post.postDescription || 'No description'}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`platform-badge platform-${post.platform.toLowerCase()}`}>
                                                {post.platform}
                                            </span>
                                        </td>
                                        <td>{formatDate(post.postedAt)}</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <TrendingUp size={16} />
                                                <span>{post.commentsCount}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${post.status.toLowerCase()}`}>
                                                {post.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                {post.permalink && (
                                                    <a
                                                        href={post.permalink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn-outline"
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '0.5rem 1rem' }}
                                                    >
                                                        <Eye size={16} />
                                                        View
                                                    </a>
                                                )}
                                                {(post.platform.toUpperCase() === 'INSTAGRAM' || post.platform.toUpperCase() === 'FACEBOOK' || post.platform.toUpperCase() === 'TIKTOK' || post.platform.toUpperCase() === 'TWITTER') && (
                                                    <button
                                                        className="btn-outline"
                                                        onClick={() => handleSync(post.postId, post.platform)}
                                                        disabled={syncingMap[post.postId]}
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '0.5rem 1rem' }}
                                                        title="Sync this post"
                                                    >
                                                        <RefreshCw size={16} className={syncingMap[post.postId] ? "spin" : ""} />
                                                        {syncingMap[post.postId] ? 'Syncing...' : 'Sync'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {!loading && total > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', flexWrap: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1.5rem', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.875rem', color: '#64748b', whiteSpace: 'nowrap' }}>Rows per page:</span>
                        <select
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.875rem',
                                color: '#475569',
                                backgroundColor: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <span style={{ fontSize: '0.875rem', color: '#64748b', marginRight: '1rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} results
                    </span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="icon-btn"
                            style={{
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                cursor: page === 1 ? 'not-allowed' : 'pointer',
                                opacity: page === 1 ? 0.5 : 1
                            }}
                        >
                            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>‹</span>
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b', padding: '0 0.5rem' }}>
                                Page {page} of {Math.ceil(total / limit)}
                            </span>
                        </div>
                        <button
                            onClick={() => setPage(p => Math.min(Math.ceil(total / limit), p + 1))}
                            disabled={page >= Math.ceil(total / limit)}
                            className="icon-btn"
                            style={{
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                                opacity: page >= Math.ceil(total / limit) ? 0.5 : 1
                            }}
                        >
                            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>›</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PostList;
